import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/nexus-utilities';
import { environment } from '@environments/environment';
import { BatchConsumptionEstimate } from '@app/models/dyeing-dashboard/batch-consumption-estimate';
import { ItemConsumption, ProductionCardDyeingCycle } from '@app/models/dyeing-dashboard/production-card-dyeing-cycle';

@Injectable({
  providedIn: 'root',
})
export class ProductionCardDyeingCycleProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.productionApi + '/api/v1/production-card-dyeing-cycle';

  startCycle(productionCardReference: string, stationAssetId: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.post<ApiResponse<ProductionCardDyeingCycle>>(
      `${this.baseUrl}/production-card/${productionCardReference}/start-cycle?stationAssetId=${stationAssetId}`,
      {},
    );
  }

  endCycle(id: string, stationAssetId: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.patch<ApiResponse<ProductionCardDyeingCycle>>(
      `${this.baseUrl}/${id}/end-cycle?stationAssetId=${stationAssetId}`,
      {},
    );
  }

  hardApprove(id: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.patch<ApiResponse<ProductionCardDyeingCycle>>(`${this.baseUrl}/${id}/hard-approve`, {});
  }

  hardStop(id: string, comments?: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.patch<ApiResponse<ProductionCardDyeingCycle>>(`${this.baseUrl}/${id}/hard-stop`, { comments });
  }

  unloadCycle(id: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.patch<ApiResponse<ProductionCardDyeingCycle>>(`${this.baseUrl}/${id}/unload-cycle`, {});
  }

  qualityControlApproval(id: string, isApproved: boolean, comments?: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.post<ApiResponse<ProductionCardDyeingCycle>>(`${this.baseUrl}/${id}/quality-control`, {
      isApproved,
      comments,
    });
  }

  getByProductionCardId(productionCardId: string): Observable<ApiResponse<ProductionCardDyeingCycle[]>> {
    return this.httpClient.get<ApiResponse<ProductionCardDyeingCycle[]>>(`${this.baseUrl}/production-card/${productionCardId}`);
  }

  getById(id: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.get<ApiResponse<ProductionCardDyeingCycle>>(`${this.baseUrl}/${id}`);
  }

  startCycleByBatch(batchId: string, stationAssetId: string): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.post<ApiResponse<ProductionCardDyeingCycle>>(
      `${this.baseUrl}/batch/${batchId}/start-cycle?stationAssetId=${stationAssetId}`,
      {},
    );
  }

  getByInitiator(reference: string, entityDefinitionCode: number): Observable<ApiResponse<ProductionCardDyeingCycle[]>> {
    return this.httpClient.request<ApiResponse<ProductionCardDyeingCycle[]>>(
      'GET',
      `${this.baseUrl}/initiator?reference=${reference}&entityDefinitionCode=${entityDefinitionCode}`,
    );
  }

  updateItemConsumptions(
    id: string,
    itemConsumptions: ItemConsumption[],
    waterUsageInLiters: number,
  ): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.patch<ApiResponse<ProductionCardDyeingCycle>>(
      `${this.baseUrl}/${id}/item-consumptions?waterUsageInLiters=${waterUsageInLiters}`,
      itemConsumptions,
    );
  }
  getConsumptionEstimate(id: string): Observable<ApiResponse<BatchConsumptionEstimate>> {
    return this.httpClient.get<ApiResponse<BatchConsumptionEstimate>>(`${this.baseUrl}/${id}/consumption-estimate`);
  }

  updateWaterUsage(id: string, waterUsageInLiters: number): Observable<ApiResponse<ProductionCardDyeingCycle>> {
    return this.httpClient.patch<ApiResponse<ProductionCardDyeingCycle>>(`${this.baseUrl}/${id}/water-usage`, { waterUsageInLiters });
  }
}
